import React, { useEffect, useState } from 'react';

/**
 * AcknowledgmentPulse — a soft phase-tinted ripple that emanates from
 * the Living Orb whenever a user completes a save action (log symptoms,
 * upload report, complete check-in, finish Body Snapshot). Replaces
 * generic toasts so every acknowledgment ties back to the hero.
 *
 * Usage:
 *   const { pulse } = useAcknowledgmentPulse();
 *   // On save:
 *   pulse();
 *
 * Renders a full-screen overlay (portal-friendly, non-interactive) that
 * plays a 900ms animation then unmounts. Cheap: pure CSS transform +
 * opacity, respects `prefers-reduced-motion`.
 */

let listeners = new Set();
export function triggerAcknowledgmentPulse(opts = {}) {
  listeners.forEach((fn) => fn(opts));
}
export function useAcknowledgmentPulse() {
  return { pulse: (opts) => triggerAcknowledgmentPulse(opts) };
}

export default function AcknowledgmentPulseHost() {
  const [pulses, setPulses] = useState([]);

  useEffect(() => {
    const handler = (opts) => {
      const id = Math.random().toString(36).slice(2, 8);
      setPulses((p) => [...p, { id, ...opts }]);
      setTimeout(() => setPulses((p) => p.filter(x => x.id !== id)), 1100);
    };
    listeners.add(handler);
    return () => { listeners.delete(handler); };
  }, []);

  if (!pulses.length) return null;

  return (
    <div className="ack-pulse-host" data-testid="ack-pulse-host" aria-hidden="true">
      {pulses.map((p) => (
        <div key={p.id} className="ack-pulse" data-tone={p.tone || 'brand'}>
          <div className="ack-pulse-ring ack-pulse-ring--1"/>
          <div className="ack-pulse-ring ack-pulse-ring--2"/>
          <div className="ack-pulse-ring ack-pulse-ring--3"/>
        </div>
      ))}
    </div>
  );
}
